import { useRef, useState, useEffect } from "react";

import styles from "./Chat.module.css";
import {chatApi, chatApiStream, ChatAppResponseOrError, ChatAppRequest, ResponseChoice} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import Preview from "./Preview";
import {ClearChatButton} from "../../components/ClearChatButton";
import JournalIcon from '../../assets/journal.svg?react';

import {useAuth} from "../../Auth/AuthContect";
import { Button } from "antd";

const Chat = () => {
    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const wsCleanupRef = useRef<(() => void) | null>(null);
    const {authToken, setAuthToken} = useAuth()
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(AnalysisPanelTabs.ThoughtProcessTab);
    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState< ResponseChoice[]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<ResponseChoice[]>([]);
    const [currentStatus, setCurrentStatus] = useState<string>('');
    const [currentReasoning, setCurrentReasoning] = useState<string>('');
    const [currentAnswer, setCurrentAnswer] = useState<Partial<ResponseChoice> | null>(null);
    // Store reasoning history per answer index
    const [reasoningHistory, setReasoningHistory] = useState<Record<number, string>>({});
    // Store all questions in chat history
    const [questions, setQuestions] = useState<string[]>([]);
    
    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;
        error && setError(undefined);
        setIsLoading(true);
        setIsStreaming(true);

        // Clean up any existing WebSocket connection
        if (wsCleanupRef.current) {
            wsCleanupRef.current();
            wsCleanupRef.current = null;
        }

        const request: ChatAppRequest = {
            is_new: !answers.length,
            question: question,
        };

        // Add question to history
        setQuestions(prev => [...prev, question]);
        
        // Reset streaming state for new question
        setCurrentStatus('');
        // Don't clear currentReasoning - it will be replaced by new reasoning as it streams in
        // This way the previous reasoning stays visible until new reasoning arrives
        setCurrentAnswer(null);
        setStreamedAnswers([]);
        // Don't clear reasoningHistory - preserve it for all previous answers

        // Start SSE streaming
        const cleanup = chatApiStream(request, authToken, {
            onStatus: (status: string) => {
                // Update status - this replaces the loading message
                setCurrentStatus(status);
            },
            onReasoning: (reasoning: string) => {
                // Update reasoning for ThoughtProcess - this replaces any previous reasoning
                setCurrentReasoning(reasoning);
                // Automatically open the ThoughtProcess tab when reasoning is received
                if (reasoning) {
                    setActiveAnalysisPanelTab(AnalysisPanelTabs.ThoughtProcessTab);
                }
            },
            onAnswer: (chunk: Partial<ResponseChoice>) => {
                // Update streaming answer
                setCurrentAnswer(chunk);
                // Also add to streamedAnswers for display
                const answerToDisplay: ResponseChoice = {
                    message: chunk.message || { content: '', role: 'assistant' },
                    question: question,
                    generated_question: chunk.generated_question || '',
                    source_documents: chunk.source_documents || []
                };
                setStreamedAnswers([answerToDisplay]);
            },
            onError: (error: Error) => {
                // Also add to streamedAnswers for display
                const answerToDisplay: ResponseChoice = {
                    message: { content: '', role: '' },
                    question: question,
                    generated_question: '',
                    source_documents:  []
                };
                setAnswers(prev => [...prev, answerToDisplay]);
                console.error('Streaming error:', error);
                if (error.message.includes('401') || error.message.includes('Unauthorized')) {
                    setAuthToken('');
                } else {
                    setError(error);
                }
                setIsStreaming(false);
                setIsLoading(false);
                setCurrentStatus('');
            },
            onComplete: (finalAnswer: ResponseChoice) => {
                // Move from streaming to final answers
                const newAnswerIndex = answers.length;
                setAnswers(prev => [...prev, finalAnswer]);
                
                // Save current reasoning to history for this answer
                if (currentReasoning) {
                    setReasoningHistory(prev => ({
                        ...prev,
                        [newAnswerIndex]: currentReasoning
                    }));
                }
                
                // Set selected answer to the newly completed answer
                setSelectedAnswer(newAnswerIndex);
                
                setStreamedAnswers([]);
                setCurrentAnswer(null);
                setCurrentStatus('');
                setIsStreaming(false);
                setIsLoading(false);
            },
            onClose: () => {
                setIsStreaming(false);
                setIsLoading(false);
            }
        });

        wsCleanupRef.current = cleanup;
    };

    const clearChat = () => {
        // Clean up WebSocket connection
        if (wsCleanupRef.current) {
            wsCleanupRef.current();
            wsCleanupRef.current = null;
        }
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setCurrentStatus('');
        setCurrentReasoning('');
        setCurrentAnswer(null);
        setReasoningHistory({}); // Clear reasoning history when clearing chat
        setQuestions([]); // Clear questions history when clearing chat
        setIsLoading(false);
        setIsStreaming(false);
    };

    // Cleanup WebSocket on unmount
    useEffect(() => {
        return () => {
            if (wsCleanupRef.current) {
                wsCleanupRef.current();
            }
        };
    }, []);

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [streamedAnswers]);


    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    return (
        <div className={styles.container}>

            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {questions.length === 0 ? (
                        <Preview
                            onExampleClicked={onExampleClicked}
                            makeApiRequest={makeApiRequest}
                            isLoading={isLoading}
                        />
                    ) : (
                        <div className={styles.chatMessageStream}>
                            <div className={styles.chatMessageStreamWrapper}>
                            {/* Show all questions and answers in chat history */}
                            {questions.map((question, qIndex) => (
                                <div key={`question-${qIndex}`} className={styles.chatMessageItemWrapper} >
                                    {/* Show question */}
                                        <UserChatMessage message={question} />
                                    
                                    {/* Show answer for this question if it exists */}
                                    {qIndex < answers.length && (
                                        <div className={styles.chatMessageItemWrapper}>
                                            <div className={styles.chatMessageGpt}>
                                                {answers[qIndex]?.message?.content ? <Answer
                                                    isStreaming={false}
                                                    answer={answers[qIndex]}
                                                    isSelected={selectedAnswer === qIndex && activeAnalysisPanelTab !== undefined}
                                                /> : <div/>}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Show streaming answer if this is the current question being processed */}
                                    {qIndex === questions.length - 1 && isStreaming && currentAnswer && streamedAnswers.length > 0 &&
                                        streamedAnswers.map((streamedAnswer, index) => (
                                            <div key={`streaming-${index}`} className={styles.chatMessageItemWrapper}>
                                                <div className={styles.chatMessageGpt}>
                                                    <Answer
                                                        isStreaming={true}
                                                        answer={streamedAnswer}
                                                        isSelected={false}
                                                    />
                                                </div>
                                            </div>
                                        ))
                                    }
                                    
                                    {/* Show loading/status if this is the current question */}
                                    {qIndex === questions.length - 1 && (isLoading || currentStatus) && !currentAnswer && (
                                        <div className={styles.chatMessageItemWrapper}>
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <AnswerLoading status={currentStatus} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {error ? (
                                <>
                                    {/*<UserChatMessage message={lastQuestionRef.current} />*/}
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError
                                            error={'Sorry, the system is currently not responding. Please try again later.'}
                                            onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                            </div>
                            <div className={styles.chatInput}>
                                <QuestionInput
                                    clearOnSend
                                    placeholder="Type a new question here..."
                                    disabled={isLoading || isStreaming}
                                    onSend={question => makeApiRequest(question)}
                                    inside
                                />
                            </div>
                            <div className={styles.bottomButtons}>
                                <div className={`${styles.containerBtnInspect}`}>
                                    <Button icon={<JournalIcon />} onClick={() => {
                                        if(activeAnalysisPanelTab) setActiveAnalysisPanelTab(undefined)
                                        else setActiveAnalysisPanelTab(AnalysisPanelTabs.ThoughtProcessTab)
                                    }} disabled={!answers.length}  type="text" className={styles.inspectButton}>
                                      {activeAnalysisPanelTab ? 'Hide Inspect Panel' : "Show Inspect Panel"}
                                    </Button>
                                </div>
                            <ClearChatButton className={styles.commandButton}
                                             onClick={clearChat}
                                             disabled={!lastQuestionRef?.current || isLoading || isStreaming} />
                            </div>
                        </div>
                    )}
                </div>

                {((answers.length > 0 || currentReasoning) && activeAnalysisPanelTab) && (() => {
                    // Determine which answer to show (selected or last)
                    const displayAnswerIndex = selectedAnswer >= 0 && selectedAnswer < answers.length 
                        ? selectedAnswer 
                        : answers.length - 1;
                    
                    // Show current reasoning if streaming, otherwise show from history
                    const displayReasoning = currentReasoning || reasoningHistory[displayAnswerIndex];
                    
                    return (
                        <AnalysisPanel
                            className={styles.chatAnalysisPanel}
                            onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                            citationHeight="810px"
                            answer={answers[displayAnswerIndex] || {
                                message: { content: '', role: 'assistant' },
                                question: lastQuestionRef.current || '',
                                generated_question: '',
                                source_documents: []
                            }}
                            reasoning={displayReasoning}
                            activeTab={activeAnalysisPanelTab}
                            closePanel={() => setActiveAnalysisPanelTab(undefined)}
                        />
                    );
                })()}
            </div>
        </div>
    );
};

export default Chat;
